import { Component, Input, Output, EventEmitter, OnInit, HostListener, ElementRef } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';

@Component({
  selector: 'app-initial-form-dropdown',
  templateUrl: './initial-form-dropdown.component.html',
  styleUrls: ['./initial-form-dropdown.component.scss']
})
export class InitialFormDropdownComponent implements OnInit {
  @Input() control!: FormControl | FormArray;
  @Input() label: string = '';
  @Input() options: { value: string; label: string }[] = [];
  @Input() multiSelect: boolean = false;
  @Input() maxSelections: number = 2;
  @Input() enableSearch: boolean = false;

  @Output() selectionChange = new EventEmitter<string | string[]>();

  dropdownOpen = false;
  openUpwards = false;
  searchQuery = '';
  filteredOptions: { value: string; label: string }[] = [];

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    this.resetFilteredOptions();
  }

  toggleDropdown(): void {
    if (this.control.disabled) return;

    this.dropdownOpen = !this.dropdownOpen;
    this.resetFilteredOptions();

    if (this.dropdownOpen) {
      setTimeout(() => this.adjustDropdownDirection());
    }
  }

  selectOption(value: string): void {
    this.multiSelect ? this.handleMultiSelect(value) : this.handleSingleSelect(value);

    this.control.markAsTouched();
    this.selectionChange.emit(this.control.value);
  }

  filterOptions(): void {
    const filtered = this.options.filter(option =>
      option.label.toLowerCase().includes(this.searchQuery.toLowerCase())
    );
    this.filteredOptions = this.sortOptionsBySelection(filtered);
  }

  deselectAll(event: Event): void {
    event.stopPropagation();
    this.updateControlValue(this.multiSelect ? [] : '');
    this.selectionChange.emit(this.control.value);
  }

  removeSelectedItem(value: string, event: Event): void {
    event.stopPropagation();
    if (this.multiSelect) {
      this.updateMultiSelectValue(value);
      this.selectionChange.emit(this.control.value);
    }
  }

  @HostListener('document:click', ['$event'])
  closeDropdownOutside(event: Event): void {
    if (this.dropdownOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.dropdownOpen = false;
    }
  }

  // --- Private Helpers ---

  private handleSingleSelect(value: string): void {
    const currentValue = (this.control as FormControl).value;
    this.updateControlValue(currentValue === value ? '' : value);
    this.dropdownOpen = false;
  }

  private handleMultiSelect(value: string): void {
    if (this.control instanceof FormArray) {
      this.updateFormArray(value);
    } else if (this.control instanceof FormControl) {
      this.updateFormControlArray(value);
    }
  }

  private updateFormArray(value: string): void {
    this.removeEmptyControls();
    const controls = (this.control as FormArray).controls;
    const values = controls.map(control => control.value);

    if (values.includes(value)) {
      const index = values.indexOf(value);
      if (index !== -1) (this.control as FormArray).removeAt(index);
    } else if (values.length < this.maxSelections) {
      (this.control as FormArray).push(new FormControl(value));
      if (values.length + 1 >= this.maxSelections) {
        this.dropdownOpen = false;
      }
    }
  }

  private updateFormControlArray(value: string): void {
    let selectedValues: string[] = Array.isArray((this.control as FormControl).value)
      ? (this.control as FormControl).value
      : [];

    selectedValues = selectedValues.filter(v => v !== '');

    if (selectedValues.includes(value)) {
      selectedValues = selectedValues.filter(v => v !== value);
    } else if (selectedValues.length < this.maxSelections) {
      selectedValues.push(value);
      if (selectedValues.length >= this.maxSelections) {
        this.dropdownOpen = false;
      }
    }

    this.updateControlValue(selectedValues.length > 0 ? selectedValues : []);
  }

  private updateMultiSelectValue(value: string): void {
    if (this.control instanceof FormArray) {
      const index = this.control.controls.findIndex(control => control.value === value);
      if (index !== -1) this.control.removeAt(index);
    } else if (this.control instanceof FormControl) {
      let selected = Array.isArray(this.control.value) ? this.control.value : [];
      selected = selected.filter(val => val !== value);
      this.updateControlValue(selected.length ? selected : []);
    }
  }

  private updateControlValue(value: string | string[]): void {
    (this.control as FormControl).setValue(value);
  }

  private resetFilteredOptions(): void {
    this.filteredOptions = this.sortOptionsBySelection(this.options);
  }

  private sortOptionsBySelection(options: { value: string; label: string }[]): { value: string; label: string }[] {
    const selectedSet = new Set(this.selectedValues);
    return [
      ...options.filter(option => selectedSet.has(option.value)),
      ...options.filter(option => !selectedSet.has(option.value))
    ];
  }

  private adjustDropdownDirection(): void {
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 220;

    this.openUpwards = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;
  }

  private removeEmptyControls(): void {
    if (!(this.control instanceof FormArray)) return;

    for (let i = this.control.length - 1; i >= 0; i--) {
      if (!this.control.at(i).value) {
        this.control.removeAt(i);
      }
    }
  }

  // --- Getters ---

  get selectedValues(): string[] {
    if (this.multiSelect) {
      return Array.isArray(this.control.value) ? this.control.value.filter(v => v) : [];
    }
    return [];
  }

  getOptionLabel(value: string): string {
    return this.options.find(option => option.value === value)?.label || value;
  }

  getSelectedText(): string {
    if (this.multiSelect) {
      return (this.control.value || [])
        .filter(v => v)
        .map(v => this.getOptionLabel(v))
        .join(', ');
    }
    return this.getOptionLabel(this.control.value);
  }

  isSelected(value: string): boolean {
    return this.multiSelect ? this.control.value?.includes(value) : this.control.value === value;
  }

  getErrorMessage(): string {
    if (this.control.hasError('required')) {
      return 'Este campo es obligatorio.';
    }
    return '';
  }
}
